import { includes } from 'lodash';
import { useContext, lazy } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Switch, Route, Redirect } from 'react-router-dom';

import { useCurrentUser } from 'hooks/useCurrentUser';
import { SuspenseWrapper } from 'common/utils/route.utils';
import { useLoginPageState } from 'hooks/useLoginPageState';
import { Loading } from 'components/common/Loading/Loading';
import { HttpErrorBoundary } from 'components/HttpErrorBoundary/HttpErrorBoundary';
import { SystemSettingsContext } from 'components/providers/SystemSettingsProvider';
import { AuthenticatedUserContext } from 'components/providers/AuthenticatedUserProvider';
import { ProtectedRouteProviders } from 'components/ProtectedRoute/ProtectedRouteProviders';
import { ApplicationsFormsSchemasContext } from 'components/providers/ApplicationsFormsSchemasProvider';

import { Routes } from 'common/enum/routes';

const LoginPage = lazy(() =>
  import('pages/LoginPage/LoginPage').then((module) => ({
    default: module.LoginPage,
  })),
);
const TokenLoginPage = lazy(() =>
  import('pages/TokenLoginPage/TokenLoginPage').then((module) => ({
    default: module.TokenLoginPage,
  })),
);
const AuthErrorPage = lazy(() =>
  import('pages/AuthErrorPage/AuthErrorPage').then((module) => ({
    default: module.AuthErrorPage,
  })),
);
const OAuthErrorPage = lazy(() =>
  import('pages/OAuthErrorPage/OAuthErrorPage').then((module) => ({
    default: module.OAuthErrorPage,
  })),
);
const AccountSelectionPage = lazy(() =>
  import(
    'pages/FortiCloudAccountSelectionPage/FortiCloudAccountSelectionPage'
  ).then((module) => ({
    default: module.FortiCloudAccountSelectionPage,
  })),
);
const ProtectedRoute = lazy(() =>
  import('components/ProtectedRoute/ProtectedRoute').then((module) => ({
    default: module.ProtectedRoute,
  })),
);
const ExternalLinkPage = lazy(() =>
  import('pages/ExternalLinkPage/ExternalLinkPage').then((module) => ({
    default: module.ExternalLinkPage,
  })),
);
const ForgotPasswordPage = lazy(() =>
  import('pages/ForgotPasswordPage/ForgotPasswordPage').then((module) => ({
    default: module.ForgotPasswordPage,
  })),
);

export const AppRoutes = () => {
  const history = useHistory();
  const location = useLocation();
  const { logout, user } = useCurrentUser();
  const isLoginPath = includes(location.pathname, 'login');
  const isAuthErrorPath = includes(location.pathname, 'auth-error');
  const isOAuthErrorPath = includes(location.pathname, 'oauth-error');
  const isTokenLoginPath = includes(location.pathname, 'token-login');
  const isExternalLinkPath = includes(location.pathname, 'external-link');
  const isForgotPasswordPath = includes(location.pathname, 'forgot-password');
  const isAccountSelectionPath = includes(
    location.pathname,
    'account-selection',
  );

  const { isLoaded, isAuthenticated } = useContext(AuthenticatedUserContext);
  const { isApplicationsFormsSchemasLoading } = useContext(
    ApplicationsFormsSchemasContext,
  );

  const {
    data: { isFortiCloudMode },
    isLoading: isSystemSettingsLoading,
  } = useContext(SystemSettingsContext);

  const {
    selectedState: { isRootLogin, tenantName },
  } = useLoginPageState();

  if (
    !isLoaded ||
    isSystemSettingsLoading ||
    isApplicationsFormsSchemasLoading
  ) {
    return <Loading size={70} />;
  }

  if (isExternalLinkPath) {
    return (
      <Switch>
        <Route
          path={Routes.EXTERNAL_LINK}
          component={() => (
            <SuspenseWrapper>
              <ExternalLinkPage />
            </SuspenseWrapper>
          )}
        />
      </Switch>
    );
  }

  if (isForgotPasswordPath) {
    return (
      <Switch>
        <Route
          path={Routes.FORGOT_PASSWORD}
          component={() => (
            <SuspenseWrapper>
              <ForgotPasswordPage />
            </SuspenseWrapper>
          )}
        />
      </Switch>
    );
  }

  if (isTokenLoginPath) {
    return (
      <Switch>
        <Route
          path={Routes.TOKEN_LOGIN}
          component={() => (
            <SuspenseWrapper>
              <TokenLoginPage />
            </SuspenseWrapper>
          )}
        />
      </Switch>
    );
  }

  if (isOAuthErrorPath) {
    return (
      <Switch>
        <Route
          path={Routes.OAUTH_ERROR}
          component={() => (
            <SuspenseWrapper>
              <OAuthErrorPage />
            </SuspenseWrapper>
          )}
        />
      </Switch>
    );
  }

  if (isAuthErrorPath) {
    return (
      <Switch>
        <Route
          path={Routes.AUTH_ERROR}
          component={() => (
            <SuspenseWrapper>
              <AuthErrorPage />
            </SuspenseWrapper>
          )}
        />
      </Switch>
    );
  }

  if (isAccountSelectionPath) {
    return (
      <Switch>
        <Route
          path={Routes.ACCOUNT_SELECTION}
          component={() => (
            <SuspenseWrapper>
              <AccountSelectionPage />
            </SuspenseWrapper>
          )}
        />
      </Switch>
    );
  }

  if (!isAuthenticated) {
    return (
      <Switch>
        <Route
          path={Routes.LOGIN}
          component={() => (
            <SuspenseWrapper>
              <LoginPage />
            </SuspenseWrapper>
          )}
        />
        <Redirect from='/' to={Routes.LOGIN} />
      </Switch>
    );
  }

  if (isAuthenticated && isFortiCloudMode) {
    const isRootLoginParams = isRootLogin && !!tenantName;
    const isDifferentTenant = user?.['customer_name'] !== tenantName;

    if (isLoginPath && isRootLoginParams && isDifferentTenant) {
      logout().then(() => {
        history.push({
          pathname: Routes.LOGIN,
          search: location.search,
        });
      });

      return <Loading size={70} />;
    }
  }

  return (
    <HttpErrorBoundary>
      <ProtectedRouteProviders>
        <SuspenseWrapper>
          <ProtectedRoute />
        </SuspenseWrapper>
      </ProtectedRouteProviders>
    </HttpErrorBoundary>
  );
};
